import { createSlice } from "@reduxjs/toolkit";
import jwt_decode from "jwt-decode";
import axiosInstance from "../axiosInstance/AxiosInstance";
import { toastClick } from "../../composants/toast/CustomToast";

function isValidJSON(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

const authSliceUser = createSlice({
    name: 'authUser',
    initialState: {
        refreshExpiresIn: sessionStorage.getItem('refreshExpiresIn'),
        expiresIn: sessionStorage.getItem('expiresIn'),
        accessToken: sessionStorage.getItem('accessTokenU'),
        refreshToken: sessionStorage.getItem('refreshTokenU'),
        tokensubject: sessionStorage.getItem('subU'),
        droits: isValidJSON(sessionStorage.getItem('rolesU')) ? JSON.parse(sessionStorage.getItem('rolesU')) : [],
        user: isValidJSON(sessionStorage.getItem('userU')) ? JSON.parse(sessionStorage.getItem('userU')) : null,
        isAuth: sessionStorage.getItem('isAuthU'),
        refreshCount: Number(sessionStorage.getItem('refreshCount') || 0),
        showReloginModal: false,
    },

    reducers: {
        setExpiresIn: (state, action) => {
            state.expiresIn = sessionStorage.getItem('expiresIn')
        },
        setRefreshExpiresIn: (state, action) => {
            state.refreshExpiresIn = sessionStorage.getItem('refreshExpiresIn')
        },
        setAccessToken: (state, action) => {
            state.accessToken = sessionStorage.getItem('accessTokenU')
        },
        setRefreshToken: (state, action) => {
            state.refreshToken = sessionStorage.getItem('refreshTokenU')
        },
        setSubject: (state, action) => {
            state.tokensubject = sessionStorage.getItem('subU')
        },
        setDroits: (state, action) => {
            state.droits = isValidJSON(sessionStorage.getItem('rolesU')) ? JSON.parse(sessionStorage.getItem('rolesU')) : [];
        },
        setUser: (state, action) => {
            state.user = isValidJSON(sessionStorage.getItem('userU')) ? JSON.parse(sessionStorage.getItem('userU')) : null;
            state.isAuth = state.user ? state.user.isFirstConnexion : false;
        },
        logOut: (state, action) => {
            sessionStorage.clear()
            state.droits = [];
            state.tokensubject = null;
            state.accessToken = null;
            state.user = null;
            state.refreshCount = 0;
            state.showReloginModal = false;
            state.isAuth = false;
            state.expiresIn = null;
            state.refreshExpiresIn = null;
        },
        incrementRefreshCount: (state) => {
            state.refreshCount += 1;
            sessionStorage.setItem('refreshCount', state.refreshCount);
        },
        resetRefreshCount: (state) => {
            state.refreshCount = 0;
            sessionStorage.setItem('refreshCount', '0');
        },
        setShowReloginModal: (state, action) => {
            state.showReloginModal = action.payload;
        },
        setNewTokens: (state, action) => {
            const { accessToken, refreshToken, expiresIn, refreshExpiresIn } = action.payload;
            state.accessToken = accessToken;
            state.refreshToken = refreshToken;
            state.expiresIn = expiresIn;
            state.refreshExpiresIn = refreshExpiresIn;
        },
        getToken: (state) => {
            return state.accessToken;
        }
    }
})


export const { setRefreshToken, setAccessToken, setDroits, logOut, setSubject, setUser, getToken, setExpiresIn, setRefreshExpiresIn, incrementRefreshCount, resetRefreshCount, setShowReloginModal, setNewTokens } = authSliceUser.actions

// ─── Thunk: Refresh du token ────────────────────────────────────────────────
export const refreshUserToken = () => async (dispatch, getState) => {
    const state = getState();
    const refreshCount = state.authUser.refreshCount;
    const refreshToken = state.authUser.refreshToken || sessionStorage.getItem('refreshTokenU');

    // Après 10 rafraîchissements → afficher le modal de reconnexion
    if (refreshCount >= 10) {
        dispatch(setShowReloginModal(true));
        return null; // Indique l'échec du refresh
    }

    if (!refreshToken) {
        dispatch(logOut());
        window.location.href = '/login';
        return null;
    }

    try {
        const response = await axiosInstance().post(`api/auth/refresh?refresh=${encodeURIComponent(refreshToken)}`);
        const data = response.data;

        const newAccessToken = data.access_token;
        const newRefreshToken = data.refresh_token;
        const newExpiresIn = data.expires_in;
        const newRefreshExpiresIn = data.refresh_expires_in;

        // Mettre à jour la sessionStorage
        sessionStorage.setItem('accessTokenU', newAccessToken);
        sessionStorage.setItem('refreshTokenU', newRefreshToken);
        sessionStorage.setItem('expiresIn', newExpiresIn);
        sessionStorage.setItem('refreshExpiresIn', newRefreshExpiresIn);

        // Mettre à jour le state Redux
        dispatch(incrementRefreshCount());
        dispatch(setNewTokens({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            expiresIn: newExpiresIn,
            refreshExpiresIn: newRefreshExpiresIn,
        }));

        return newAccessToken; // Retourne le nouveau token pour rejouer la requête
    } catch (error) {
        // En cas d'échec total du refresh → déconnexion
        dispatch(logOut());
        window.location.href = '/login';
        return null;
    }
};

// ─── Actions existantes ───────────────────────────────────────────────────────
export const loginUserClient = (identifier, password, setLoading) => async dispatch => {
    await axiosInstance().post("public/api/auth/login", {
        login: identifier,
        password: password
    }).then(response => {
        const accessToken = response.data.access_token;
        const refreshToken = response.data.refresh_token;
        const permissions = response.data.permissions;
        const expiresIn = response.data.expires_in;
        const refreshExpiresIn = response.data.refresh_expires_in;
        const user = response.data.user;
        let decodedToken;
        try {
            decodedToken = jwt_decode(accessToken);
            // Vérifier si le token est expiré
            const currentTime = Math.floor(Date.now() / 1000);
            if (decodedToken.exp < currentTime) {
                toastClick("Token expiré, veuillez vous reconnecter", "error");
                setLoading(false);
                return;
            }
        } catch (error) {
            toastClick("Erreur lors du décodage du token", "error");
            setLoading(false);
            return;
        }
        sessionStorage.setItem('accessTokenU', accessToken);
        sessionStorage.setItem('refreshTokenU', refreshToken);
        sessionStorage.setItem('rolesU', JSON.stringify(permissions));
        sessionStorage.setItem('subU', decodedToken.sub);
        sessionStorage.setItem('userU', JSON.stringify(user));
        sessionStorage.setItem('isAuthU', user.isFirstConnexion);
        sessionStorage.setItem('expiresIn', expiresIn);
        sessionStorage.setItem('refreshExpiresIn', refreshExpiresIn);
        // Réinitialiser le compteur de refresh à la connexion
        sessionStorage.setItem('refreshCount', '0');

        if (response.data.user.isFirstConnexion)
            sessionStorage.setItem('passwordU', password)
        dispatch(setAccessToken(sessionStorage.getItem('accessTokenU')));
        dispatch(setRefreshToken(sessionStorage.getItem('refreshTokenU')));
        dispatch(setDroits(sessionStorage.getItem('rolesU')));
        dispatch(setSubject(sessionStorage.getItem('subU')));
        dispatch(setUser(JSON.parse(sessionStorage.getItem('userU'))));
        dispatch(setExpiresIn(sessionStorage.getItem('expiresIn')));
        dispatch(setRefreshExpiresIn(sessionStorage.getItem('refreshExpiresIn')));
        dispatch(resetRefreshCount());
        toastClick("Welcome", "success");
        window.location.href = "/";
    }).catch(error => {
        setLoading(false);
        toastClick("Login / Ou Mot de passe Invalide", "error");
    });
};

export const logOutUser = () => async dispatch => {
    dispatch(logOut(""));
};

// ─── Reconnexion depuis le modal (sans redirection) ───────────────────────────
// Même chose que loginUserClient, mais :
//  • ferme le modal  (setShowReloginModal(false))
//  • reste sur la page courante  (pas de window.location.href)
export const reloginUser = (identifier, password, setLoading) => async dispatch => {
    await axiosInstance().post("public/api/auth/login", {
        login: identifier,
        password: password
    }).then(response => {
        const accessToken = response.data.access_token;
        const refreshToken = response.data.refresh_token;
        const permissions = response.data.permissions;
        const expiresIn = response.data.expires_in;
        const refreshExpiresIn = response.data.refresh_expires_in;
        const user = response.data.user;
        let decodedToken;
        try {
            decodedToken = jwt_decode(accessToken);
            const currentTime = Math.floor(Date.now() / 1000);
            if (decodedToken.exp < currentTime) {
                toastClick("Token expiré, veuillez vous reconnecter", "error");
                setLoading(false);
                return;
            }
        } catch (error) {
            toastClick("Erreur lors du décodage du token", "error");
            setLoading(false);
            return;
        }

        // Mise à jour de la sessionStorage
        sessionStorage.setItem('accessTokenU', accessToken);
        sessionStorage.setItem('refreshTokenU', refreshToken);
        sessionStorage.setItem('rolesU', JSON.stringify(permissions));
        sessionStorage.setItem('subU', decodedToken.sub);
        sessionStorage.setItem('userU', JSON.stringify(user));
        sessionStorage.setItem('isAuthU', user.isFirstConnexion);
        sessionStorage.setItem('expiresIn', expiresIn);
        sessionStorage.setItem('refreshExpiresIn', refreshExpiresIn);
        sessionStorage.setItem('refreshCount', '0');

        if (user.isFirstConnexion === false)
            sessionStorage.setItem('passwordU', password);

        // Mise à jour du store Redux
        dispatch(setAccessToken());
        dispatch(setRefreshToken());
        dispatch(setDroits());
        dispatch(setSubject());
        dispatch(setUser());
        dispatch(setExpiresIn());
        dispatch(setRefreshExpiresIn());
        dispatch(resetRefreshCount());
        // Fermer le modal et rester sur la page courante
        dispatch(setShowReloginModal(false));

        toastClick("Reconnexion réussie ✓", "success");
        // ← PAS de window.location.href ici
    }).catch(() => {
        setLoading(false);
        toastClick("Login / Mot de passe invalide", "error");
    });
};

export const setImage = (data) => dispatch => {
    sessionStorage.setItem('userU', JSON.stringify(data));
    dispatch(setUser(data));
};
export const setInfos = (nom, prenom, user) => async dispatch => {
    user.nom = nom;
    user.prenom = prenom;
    sessionStorage.setItem('userU', JSON.stringify(user));
};

export default authSliceUser.reducer;
